
import { Component } from 'vue-property-decorator'
import moment from 'moment'

import { AbstractReportComponent } from './AbstractReportComponent'
import TableKpiCompareItem from '@/components/dashboard/TableKpiCompareItem.vue'
import { KpiItem, FiltersDateFormats, KpisStatusManagement, KpisUserStatusManagement, KpisStatusManagementData } from '@/types/Dashboard'
import PieChart from '@/components/charts/PieChart.vue'
import BarChart from '@/components/charts/BarChart.vue'
import Colors from '@/types/Colors'
import reportsService from '@/services/ReportsService'

@Component({
  components: {
    TableKpiCompareItem,
    PieChart,
    BarChart
  },
  filters: {
    formatNum (value: number) {
      return value.toLocaleString('en')
    }
  }
})
export default class ReportEstatusGestion extends AbstractReportComponent {
  usersData = new KpisStatusManagementData()
  mainKpis = new KpisStatusManagement()

  // Main KPIs
  kpis: KpiItem[] = []

  searchUsers = ''
  filterWorkingUsers = false
  filterNotWorkingUsers = false
  filterNotAssignedUsers = false
  filterWorkingToday = false

  tableUsernameSortDirAsc = true
  tableAmountSortDirAsc = false

  // Charts
  refreshingCharts = false
  datacollection: any = {
    workedUsers: null,
    statusTasks: null
  }

  options = {
    responsive: true,
    maintainAspectRatio: false,
    tooltips: {
      callbacks: {
        label: (tooltipItem: any, data: any) => {
          let valueNum = Number(data.datasets[0].data[tooltipItem.index] || 0)
          return valueNum.toLocaleString('en')
        }
      }
    },
    plugins: {
      datalabels: {
        anchor: 'begin',
        align: 'end',
        textAlign: 'center',
        color: '#fff',
        textShadowBlur: 3,
        textShadowColor: '#000',
        font: {
          weight: 'bold',
          size: 14
        },
        formatter: (value: any, ctx: any) => {
          if (!value) return ''
          let datasets = ctx.chart.data.datasets
          let percentage = ''
          if (datasets.indexOf(ctx.dataset) === datasets.length - 1) {
            let sum = datasets[0].data.reduce((a: number, b: number) => a + b, 0)
            percentage = value.toLocaleString('en') + '\n(' + Math.round((value / sum) * 100) + '%)'
            return percentage
          } else {
            return percentage
          }
        }
      }
    }
  }

  optionsUsersChart = {
    legend: { display: false },
    responsive: true,
    maintainAspectRatio: false,
    tooltips: {
      callbacks: {
        label: (tooltipItem: any, data: any) => {
          let valueNum = Number(data.datasets[0].data[tooltipItem.index] || 0)
          return valueNum.toLocaleString('en')
        }
      }
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          precision: 0
        }
      }]
    },
    plugins: {
      datalabels: {
        color: '#fff',
        textShadowBlur: 4,
        textShadowColor: '#000',
        font: {
          weight: 'bold',
          size: 15
        },
        align: 'top',
        anchor: 'begin',
        formatter: (value: any, ctx: any) => {
          let datasets = ctx.chart.data.datasets
          let percentage = ''
          if (datasets.indexOf(ctx.dataset) === datasets.length - 1) {
            let sum = datasets[0].data.reduce((a: number, b: number) => a + b, 0)
            percentage = value + ' (' + Math.round((value / sum) * 100) + '%)'
            return percentage
          } else {
            return percentage
          }
        }
      }
    }
  }

  keyCharts = 0

  created () {
    this.$store.commit('global/updateFiltersReportEstatusGestion')
  }

  mounted () {
    this.initialPageTitle = document.title
    this.filter(this.$route.query as any)
    this.$root.$on('export-report', this.exportReport)
    window.addEventListener('afterprint', this.afterPrintPdfReport)
  }

  beforeDestroy () {
    document.title = this.initialPageTitle
    this.$root.$off('export-report', this.exportReport)
    window.removeEventListener('afterprint', this.afterPrintPdfReport)
  }

  get filtersRequiredSet () {
    return !!this.filters.template && !!this.filters.group
  }

  resetReportValues () {
    this.usersData = new KpisStatusManagementData()
    this.mainKpis = new KpisStatusManagement()
    this.kpis = []
  }

  async refreshData () {
    if (!this.filtersRequiredSet) return

    this.loadingData = true
    this.$store.commit('global/lockReportFilters', true)

    this.tableUsernameSortDirAsc = true
    this.tableAmountSortDirAsc = false

    try {
      await reportsService.getEstatusGestion(this.filters)
        .then((response) => { this.usersData = response })
        .catch(() => {
          this.endPrint()
          this.$store.commit('global/lockReportFilters', false)
          this.resetReportValues()
        })

      this.sortAmountByManagedTasks()

      const kpis = reportsService.getStatusManagementKpis(this.usersData)
      this.mainKpis = kpis
      this.refreshMainKpis(kpis)
      this.refreshChartsKpis(kpis)
    } finally {
      setTimeout(() => {
        this.loadingData = false
        this.$store.commit('global/lockReportFilters', false)
      }, 500)
    }
  }

  refreshFiltersInfo () {
    let result = []

    if (this.filtersStore.template) {
      result.push(this.filtersStore.templateName)
    }

    if (this.filtersStore.group) {
      result.push('Grupo: ' + this.filtersStore.groupName)
    }

    const date = 'Del ' + moment(this.filtersStore.endDate, FiltersDateFormats.QUERY_STRING).format('D [de] MMMM [de] YYYY')
    result.push(date)

    this.filtersHead = result.join(', ')
  }

  refreshMainKpis (kpis: KpisStatusManagement) {
    this.kpis = []

    this.kpis.push(
      KpiItem.getInstance().withName('Total<br>de tareas').withValue((kpis.sumManagedTasks || 0) + (kpis.sumPendingManagement || 0) + (kpis.unassignedTasks || 0)).withIcon('fa-clipboard-list').withColor('aqua')
    )

    this.kpis.push(
      KpiItem.getInstance().withName('Tareas gestionadas<br>en el periodo').withValue(kpis.sumManagedTasks).withIcon('fa-map-marker-check').withColor('green')
    )

    this.kpis.push(
      KpiItem.getInstance().withName('Tareas pendientes<br>de gestionar').withValue(kpis.sumPendingManagement).withIcon('fa-map-marker-exclamation').withColor('yellow')
    )

    if (kpis.unassignedTasks !== undefined) {
      this.kpis.push(
        KpiItem.getInstance().withName('Tareas pendientes<br>de asignar').withValue(kpis.unassignedTasks || 0).withIcon('fa-clipboard-list-check').withColor('gray')
      )
    }

    this.kpis.push(
      KpiItem.getInstance().withName(`Usuarios<br>productivos`).withValue(kpis.sumActiveUsers).withIcon('fa-street-view').withColor('purple')
    )
  }

  refreshChartsKpis (kpis: KpisStatusManagement) {
    this.refreshingCharts = true

    this.datacollection.workedUsers = {
      labels: ['Usuarios gestionando', 'Usuarios sin gestión', 'Usuarios sin asignación'],
      datasets: [
        {
          backgroundColor: [ Colors.green[1], Colors.yellow[1], Colors.gray[0] ],
          data: [ kpis.sumWorkingUsers, kpis.sumNotWorkingUsers, kpis.sumNotAssignedUsers ]
        }
      ]
    }

    let statusTasksLabels = ['Realizadas', 'Pendientes de gestionar']
    let statusTasksData = [ kpis.sumManagedTasks, kpis.sumPendingManagement ]
    let statusTasksBackgroundColor = [ Colors.green[0], Colors.yellow[1] ]

    if (kpis.unassignedTasks !== undefined) {
      statusTasksBackgroundColor.push(Colors.gray[0])
      statusTasksLabels.push('Pendientes de asignar')
      statusTasksData.push(kpis.unassignedTasks)
    }

    this.datacollection.statusTasks = {
      labels: statusTasksLabels,
      datasets: [
        {
          label: 'Tareas',
          backgroundColor: statusTasksBackgroundColor,
          data: statusTasksData
        }
      ]
    }

    setTimeout(() => {
      this.refreshingCharts = false
    }, 500)
  }

  visibleUser (userItem: KpisUserStatusManagement) {
    let visible = !this.searchUsers || userItem.user.includes(this.searchUsers)
    if (visible && (this.filterWorkingUsers || this.filterWorkingToday || this.filterNotWorkingUsers || this.filterNotAssignedUsers)) {
      visible = (this.filterWorkingUsers && userItem.isWorkingUser) ||
                (this.filterWorkingToday && userItem.managed_today > 0) ||
                (this.filterNotWorkingUsers && userItem.isNotWorkingUser) ||
                (this.filterNotAssignedUsers && userItem.isNotAssignedUser)
    }
    return visible
  }

  sortAmountByManagedTasks () {
    if (this.tableAmountSortDirAsc) {
      this.usersData.users.sort((a, b) => (a.managed > b.managed) ? 1 : -1)
    } else {
      this.usersData.users.sort((a, b) => (a.managed < b.managed) ? 1 : -1)
    }
    this.tableAmountSortDirAsc = !this.tableAmountSortDirAsc
  }

  sortUsernameByManagedTasks () {
    if (this.tableUsernameSortDirAsc) {
      this.usersData.users.sort((a, b) => (a.user > b.user) ? 1 : -1)
    } else {
      this.usersData.users.sort((a, b) => (a.user < b.user) ? 1 : -1)
    }
    this.tableUsernameSortDirAsc = !this.tableUsernameSortDirAsc
  }

  exportReport (reportType: string) {
    switch (reportType) {
      case 'pdf':
        this.generatePdfReport()
        break
      case 'csv':
        this.generateCsvReport()
        break
    }
  }

  generatePdfReport () {
    this.validateRatio()
    this.keyCharts = new Date().getTime()
    this.searchUsers = ''
    this.startPrint()
    setTimeout(() => {
      window.print()
    }, 1200)
  }

  generateCsvReport () {
    let csv = this.mainKpis.unassignedTasks !== undefined ? 'FECHA,USUARIO,ESTATUS,TOTAL TAREAS,TAREAS REALIZADAS,TAREAS REALIZADAS HOY,TAREAS PENDIENTES DE GESTIONAR,TAREAS PENDIENTES DE ASIGNAR,% TAREAS REALIZADAS,% TAREAS PENDIENTES DE GESTIONAR,% TAREAS PENDIENTES DE ASIGNAR\n'
      : 'FECHA,USUARIO,ESTATUS,TOTAL TAREAS,TAREAS REALIZADAS,TAREAS REALIZADAS HOY,TAREAS PENDIENTES DE GESTIONAR,% TAREAS REALIZADAS,% TAREAS PENDIENTES DE GESTIONAR\n'

    let totalUserTasks = 0
    let managedTaskPercent = ''
    let pendingTaskPercent = ''
    let statusUser = ''
    let period = moment(this.filtersStore.endDate, FiltersDateFormats.QUERY_STRING).format('D/MM/YYYY')
    csv += this.mainKpis.unassignedTasks !== undefined ? '' +
        `${period},Todos,--,${this.mainKpis.totalTasks},${this.mainKpis.sumManagedTasks},${this.mainKpis.sumManagedTasksToday},${this.mainKpis.sumPendingManagement},${this.mainKpis.unassignedTasks},${(this.mainKpis.sumManagedTasks / this.mainKpis.totalTasks * 100).toFixed(1)} %,${(this.mainKpis.sumPendingManagement / this.mainKpis.totalTasks * 100).toFixed(1)} %,${(this.mainKpis.unassignedTasks / this.mainKpis.totalTasks * 100).toFixed(1)} %\n`
      : `${period},Todos,--,${this.mainKpis.totalTasks},${this.mainKpis.sumManagedTasks},${this.mainKpis.sumManagedTasksToday},${this.mainKpis.sumPendingManagement},${(this.mainKpis.sumManagedTasks / this.mainKpis.totalTasks * 100).toFixed(1)} %,${(this.mainKpis.sumPendingManagement / this.mainKpis.totalTasks * 100).toFixed(1)} %\n`

    for (let it of this.usersData.users) {
      totalUserTasks = it.managed + it.pending_management
      if (totalUserTasks) {
        managedTaskPercent = (it.managed / totalUserTasks * 100).toFixed(1)
        pendingTaskPercent = (it.pending_management / totalUserTasks * 100).toFixed(1)
      } else {
        managedTaskPercent = '0.0'
        pendingTaskPercent = '0.0'
      }
      if (it.isWorkingUser) statusUser = 'GESTIONANDO'
      if (it.isNotWorkingUser) statusUser = 'SIN GESTION'
      if (it.isNotAssignedUser) statusUser = 'SIN ASIGNACION'
      csv += this.mainKpis.unassignedTasks !== undefined ? `${period},${it.user},${statusUser},${totalUserTasks},${it.managed},${it.managed_today},${it.pending_management},,${managedTaskPercent} %,${pendingTaskPercent} %\n`
        : `${period},${it.user},${statusUser},${totalUserTasks},${it.managed},${it.managed_today},${it.pending_management},${managedTaskPercent} %,${pendingTaskPercent} %\n`
    }

    const periodTitle = moment(this.filtersStore.endDate, FiltersDateFormats.QUERY_STRING).format('DDMMYYYY')

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `${periodTitle}_${this.filtersStore.templateName}_${this.filtersStore.groupName}_RptEstatusGestion.csv`
    element.click()
  }

  afterPrintPdfReport () {
    this.endPrint()
    document.title = this.initialPageTitle
  }
}
